/**
 * Reading the session's real expiry off the JWT.
 *
 * The previous source of truth was `logged_date` — the time the client believed
 * it signed in — written from eight different places in two formats, and
 * compared against a hardcoded 24 hours that had nothing to do with the token's
 * actual lifetime. The token carries its own `exp`; that is the only value the
 * server will honour, so it is the only one worth reading.
 */

/**
 * Decodes a JWT payload without verifying it.
 *
 * Verification is the server's job — the client only needs to know when to stop
 * trying. A forged token gets rejected on the next request either way.
 *
 * @returns {object|null} the claims, or null if the token is unreadable.
 */
export const decodeToken = (token) => {
  if (typeof token !== 'string' || !token) return null;

  const parts = token.split('.');
  if (parts.length !== 3) return null;

  try {
    // JWT uses base64url, which atob() does not accept: '-' and '_' have to
    // become '+' and '/', and the padding has to be restored. Skipping this
    // works for most tokens and fails mysteriously for the rest.
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

/**
 * @returns {number|null} expiry as a millisecond timestamp, or null when the
 * token is missing, unreadable, or carries no `exp`.
 */
export const getTokenExpiry = (token) => {
  const claims = decodeToken(token);
  if (!claims || typeof claims.exp !== 'number') return null;
  return claims.exp * 1000;
};

/**
 * @returns {number|null} when the session originally started, as a millisecond
 * timestamp. Tokens issued before the backend added this claim return null;
 * callers treat that as "unknown", never as "expired".
 */
export const getSessionStart = (token) => {
  const claims = decodeToken(token);
  if (!claims || typeof claims.session_started_at !== 'number') return null;
  return claims.session_started_at * 1000;
};
